import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

import BaddieSelectionModal from "../components/BaddieSelectionModal";
import VideoSelectionModal from "../components/VideoSelectionModal";
import VideoFrameSelector from "../components/VideoFrameSelector";
import ModelSelector from "../components/ModelSelector";
import AspectRatioSelector from "../components/AspectRatioSelector";

import { apiClient } from "../services/api";
import { useToast } from "../components/Toast";

import type { Video, ModelInfo } from "../types/api";
import { isImageUrl } from "../utils/videoUtils";

interface Actor {
  id: string;
  name: string;
  image: string;
  age?: number;
  vibe?: string;
}

interface CreateVideoState {
  mode?: "simple" | "full";
  preSelectedActor?: Actor;
  prefilledPrompt?: string;
  generatedImage?: {
    url: string;
    prompt: string;
  };
  referenceImage?: string;
  selectedModel?: string;
  noFilterMode?: boolean;
}

interface CreateVideoProps {
  mode?: "t2v" | "i2v";
}

// Default model info to prevent "Select a model" from ever appearing
const DEFAULT_I2V_MODEL: ModelInfo = {
  credits: 50,
  description:
    "No Filter - High-quality image-to-video generation using WAN2.2 I2V",
  logo_url:
    "https://registry.npmmirror.com/@lobehub/icons-static-png/1.64.0/files/dark/alibaba-color.png",
  name: "wan2.2-nofilter",
  public_name: "WAN 2.2 I2V (No Filter)",
  supports: ["image-to-video"],
  type: ["image-to-video"],
};

const DEFAULT_T2V_MODEL: ModelInfo = {
  credits: 320,
  description: "World's best video generation model with cinematic quality",
  logo_url:
    "https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg",
  name: "veo3",
  public_name: "Veo 3",
  supports: ["text-to-video"],
  type: ["text-to-video"],
};

const CreateVideo: React.FC<CreateVideoProps> = ({ mode: propMode }) => {
  const location = useLocation();
  const { showToast } = useToast();

  // Showcase mode - no user data
  const isFreePlan = true;
  const generationError = null as string | null;
  const resetVideoGeneration = () => {};
  const isSignedIn = false; // Showcase mode
  const batchQuantity = 1; // Fixed for showcase

  const [camera, setCamera] = useState("Default");
  const [videoStyle, setVideoStyle] = useState("Default");
  const [characterDirection, setCharacterDirection] = useState("");

  // Set default model based on mode
  const getDefaultModel = useCallback(() => {
    if (propMode === "i2v") {
      return "wan2.2-nofilter"; // Default to wan2.2-nofilter for image-to-video
    }
    return "veo3"; // Default to Veo3 for other modes
  }, [propMode]);

  const [selectedModel, setSelectedModel] = useState<string>(getDefaultModel());
  const [modelCredits, setModelCredits] = useState<Record<string, number>>({});
  // Update selected model when mode changes
  useEffect(() => {
    const defaultModel = getDefaultModel();
    console.log(`🔧 Setting default model for mode ${propMode}:`, defaultModel);
    setSelectedModel(defaultModel);
  }, [getDefaultModel, propMode]);
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);
  const [showWan25UpgradeModal, setShowWan25UpgradeModal] = useState(false);
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<"16:9" | "9:16">("16:9");

  const [linkedVideo, setLinkedVideo] = useState<Video | null>(null);
  const [showBaddieModal, setShowBaddieModal] = useState(false);
  const [showVideoSelectionModal, setShowVideoSelectionModal] = useState(false);

  const [showFrameSelector, setShowFrameSelector] = useState(false);
  const [promptEnhancer, setPromptEnhancer] = useState(true);
  const [noFilterMode, setNoFilterMode] = useState(false);

  const handleFrameSelect = (frameBase64: string) => {
    setReferenceImage(frameBase64);
    setShowFrameSelector(false);
  };

  const getCreditsRequired = () => {
    return modelCredits[selectedModel] || 50; // Default to 50 if not found
  };

  // Showcase mode - no user plans
  const hasCreatorPlanOrHigher = () => {
    return false;
  };

  // Showcase mode - no user plans
  const hasStarterPlan = () => {
    return false;
  };

  // Handle model change with wan2.5 gating
  const handleModelChange = (model: string) => {
    // Check if trying to select wan2.5
    if (model === "wan2.5" && !hasCreatorPlanOrHigher()) {
      // Show upgrade modal instead of changing the model
      setShowWan25UpgradeModal(true);
      return;
    }

    // Allow the model change
    setSelectedModel(model);
  };

  // Check if referral card should be shown
  // useEffect(() => {
  //   if (
  //     isSignedIn &&
  //     userData &&
  //     selectedModel &&
  //     modelCredits[selectedModel]
  //   ) {
  //     const creditsRequired = modelCredits[selectedModel];
  //     const availableCredits =
  //       userData.limits.max_credits - userData.limits.credits;
  const getModelType = useCallback((): "t2v" | "i2v" => {
    return propMode === "i2v" ? "i2v" : "t2v";
  }, [propMode]);

  // Note: Default model is Veo3 for t2v, wan2.2-nofilter for i2v

  // Load model credits on mount
  useEffect(() => {
    const loadModelCredits = async () => {
      try {
        const modelType = getModelType();
        console.log("🔍 Loading model credits for type:", modelType);
        const response = await apiClient.getModels({ type: modelType as "t2v" | "i2v" | "t2i" | "i2i" });
        const credits: Record<string, number> = {};
        response.models.forEach((model) => {
          credits[model.name] = model.credits;
        });
        console.log("✅ Model credits loaded successfully:", credits);
        setModelCredits(credits);
      } catch (error) {
        console.error("❌ Failed to load model credits:", error);
        console.error("Error details:", {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
        });
        // Set default credits as fallback
        setModelCredits({
          veo3: 320,
          "veo3.1": 360,
          minimax: 45,
          "wan2.2": 50,
          "wan2.2-nofilter": 50,
          "wan2.5": 50,
        });
      }
    };

    loadModelCredits().catch((error) => {
      // Double catch to ensure errors don't bubble up to error boundary
      console.error("❌ Unhandled error in loadModelCredits:", error);
    });
  }, [propMode, getModelType]);

  const getEstimatedTime = () => {
    // Different models have different generation times
    switch (selectedModel) {
      case "veo3":
      case "veo3.1":
        return "3-5 min";
      case "minimax":
        return "2-3 min";
      case "wan2.2":
        return "2-4 min";
      case "wan2.2-nofilter":
        return "5-8 min";
      default:
        return "3-5 min";
    }
  };

  const handleStartGeneration = async () => {
    // Don't proceed if no prompt is entered
    if (!characterDirection.trim()) return;

    // For i2v mode, require reference image
    if (propMode === "i2v" && !referenceImage) {
      showToast({ type: "error", title: "Please upload or select an image" });
      return;
    }

    setIsStartingGeneration(true);

    try {
      // Build redirect URL with all form data
      const currentPath = location.pathname;
      const params = new URLSearchParams({
        utm_source: 'pixelmaker_io',
        prompt: characterDirection,
        model: selectedModel,
      });

      // Add optional parameters if they're not default values
      if (aspectRatio !== "16:9") {
        params.append('aspect_ratio', aspectRatio);
      }
      if (camera !== "Default") {
        params.append('camera', camera);
      }
      if (videoStyle !== "Default") {
        params.append('video_style', videoStyle);
      }
      if ((selectedModel === "wan2.2-nofilter" || selectedModel === "wan2.5") && promptEnhancer !== undefined) {
        params.append('prompt_enhancer', promptEnhancer.toString());
      }

      // Handle reference image
      if (referenceImage) {
        // Check if it's a local path (actor/baddie selection) or base64 (uploaded image)
        if (referenceImage.startsWith('/')) {
          // It's a local path like "/baddies/baddie1.jpg" - convert to full URL
          const fullImageUrl = `${window.location.origin}${referenceImage}`;
          params.append('reference_image', fullImageUrl);
        } else if (referenceImage.startsWith('data:')) {
          // It's base64 - upload to get S3 URL
          const uploadResponse = await fetch('https://api.pixelmaker.ai/temp-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: referenceImage })
          });
          const uploadResult = await uploadResponse.json();
          if (uploadResult.url) {
            params.append('reference_image', uploadResult.url);
          }
        } else {
          // It's already a full URL (e.g., from assets)
          params.append('reference_image', referenceImage);
        }
      }

      window.location.href = `https://pixelmaker.ai${currentPath}?${params.toString()}`;
    } catch (error) {
      console.error('Failed to upload image:', error);
      showToast({ type: "error", title: "Failed to upload image. Please try again." });
      setIsStartingGeneration(false);
    }
  };

  // Track page view (disabled for showcase)
  useEffect(() => {
    // Analytics removed for showcase site
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Showcase mode - generation state handlers removed

  // Helper function to check if error is content policy related
  const isContentPolicyError = (errorMessage: string): boolean => {
    const contentPolicyKeywords = [
      "sensitive words",
      "content policy",
      "violate",
      "responsible ai",
      "google's responsible ai practices",
      "support codes",
      "deleted",
    ];

    return contentPolicyKeywords.some((keyword) =>
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  // Showcase mode - error handling removed

  // Removed - not needed in showcase mode

  // Handle navigation state for mode and pre-selected actor
  useEffect(() => {
    // First try to restore progress from localStorage
    const saved = localStorage.getItem("createVideoProgress");
    let progressRestored = false;

    if (saved) {
      try {
        const progress = JSON.parse(saved);
        // Only restore if saved within last 30 minutes
        if (Date.now() - progress.timestamp < 30 * 60 * 1000) {
          setCamera(progress.camera || "Default");
          setVideoStyle(progress.videoStyle || "Default");
          setCharacterDirection(progress.characterDirection || "");
          setLinkedVideo(progress.linkedVideo || null);
          localStorage.removeItem("createVideoProgress");
          progressRestored = true;
        }
      } catch (error) {
        console.error("Failed to restore progress:", error);
      }
      if (!progressRestored) {
        localStorage.removeItem("createVideoProgress");
      }
    }

    // If no progress was restored, check for navigation state
    if (!progressRestored) {
      const state = location.state as CreateVideoState;
      if (state) {
        if (state.prefilledPrompt) {
          setCharacterDirection(state.prefilledPrompt);
        }
        if (state.referenceImage) {
          setReferenceImage(state.referenceImage);
        }
        if (state.selectedModel && state.selectedModel !== getDefaultModel()) {
          // Only restore if it's different from the current default
          setSelectedModel(state.selectedModel);
          console.log(
            "🔄 Restored non-default model from navigation state:",
            state.selectedModel
          );
        }
        if (state.generatedImage) {
          // Set the generated image as reference image and pre-fill prompt
          // Convert generated image URL to base64
          const convertGeneratedImage = async () => {
            try {
              const response = await fetch(state.generatedImage!.url);
              const blob = await response.blob();
              const reader = new FileReader();
              
              reader.onloadend = () => {
                try {
                  const result = reader.result as string;
                  if (result) {
                    setReferenceImage(result);
                  } else {
                    console.error("FileReader result is empty, using URL fallback");
                    setReferenceImage(state.generatedImage!.url);
                  }
                } catch (error) {
                  console.error("Error processing generated image:", error);
                  setReferenceImage(state.generatedImage!.url);
                }
              };
              
              reader.onerror = (error) => {
                console.error("FileReader error for generated image:", error);
                setReferenceImage(state.generatedImage!.url);
              };
              
              reader.readAsDataURL(blob);
            } catch (error) {
              console.error(
                "Failed to convert generated image to base64:",
                error
              );
              // Fallback to URL (backend might handle it)
              setReferenceImage(state.generatedImage!.url);
            }
          };
          convertGeneratedImage();

          if (!state.prefilledPrompt) {
            // Use the image generation prompt as a starting point for animation
            setCharacterDirection(
              `Animate this image: ${state.generatedImage.prompt}`
            );
          }
        }
        // Set noFilterMode if it's enabled (locks model selection)
        // This only applies on initial navigation, not on refresh
        if (state.noFilterMode) {
          setNoFilterMode(true);
        }

        // Clear the navigation state to prevent it from persisting on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, getDefaultModel]);

  const handleSelectLinkedVideo = async (video: Video) => {
    if (!video.url) return;

    setShowVideoSelectionModal(false);

    // Check if the selected asset is an image or video
    if (isImageUrl(video.url)) {
      // It's an image - convert to base64 before using as reference image
      try {
        const response = await fetch(video.url);
        const blob = await response.blob();
        const reader = new FileReader();
        
        reader.onloadend = () => {
          try {
            const result = reader.result as string;
            if (result) {
              setReferenceImage(result);
            } else {
              console.error("FileReader result is empty, using URL fallback");
              setReferenceImage(video.url);
            }
          } catch (error) {
            console.error("Error processing asset image:", error);
            setReferenceImage(video.url);
          }
        };
        
        reader.onerror = (error) => {
          console.error("FileReader error for asset image:", error);
          setReferenceImage(video.url);
        };
        
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Failed to convert asset image to base64:", error);
        // Fallback to URL (backend might handle it)
        setReferenceImage(video.url);
      }
      setLinkedVideo(null); // Clear linked video since we're using an image
      // Analytics removed for showcase site
    } else {
      // It's a video - show frame selector
      setLinkedVideo(video);
      setShowFrameSelector(true);
      setReferenceImage(null); // Clear any existing reference image
      // Analytics removed for showcase site
    }

    // Clear any uploaded reference image since they're mutually exclusive
    const fileInput = document.getElementById(
      "image-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Removed - not needed in showcase mode

  // Removed - not needed in showcase mode

  // Showcase mode - no generation views

  // Render different workflows based on mode
  const renderWorkflow = () => {
    if (propMode === "t2v") {
      return (
        <div className="space-y-6">
          {/* Page Header */}
          <div className="text-left mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Text to Video
            </h1>
            <p className="text-gray-600 dark:text-white text-lg">
              Transform your written ideas into engaging video content with
              AI-powered generation.
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Model Selection */}
            <div>
              <ModelSelector
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                type={getModelType()}
                defaultModelInfo={DEFAULT_T2V_MODEL}
              />
            </div>

            {/* Aspect Ratio Selection - Only for Veo3 and Veo3.1 */}
            {(selectedModel === "veo3" || selectedModel === "veo3.1") && (
              <div>
                <AspectRatioSelector
                  selectedAspectRatio={aspectRatio}
                  onAspectRatioChange={setAspectRatio}
                />
              </div>
            )}

            {/* Prompt Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Your Prompt
              </label>
              <textarea
                value={characterDirection}
                onChange={(e) => setCharacterDirection(e.target.value)}
                placeholder="Describe your video. For example: A 3D image of a little girl, wearing a flight jacket, skateboarding in a park."
                className="w-full h-40 px-6 py-4 bg-white/5 dark:bg-white/5 border border-white/20 dark:border-white/20 rounded-xl focus:outline-none focus:ring-0 focus:border-white/40 dark:focus:border-white/40 resize-none text-base leading-relaxed text-white dark:text-white placeholder-gray-400 dark:placeholder-gray-400 transition-all duration-200"
              />

              {/* Character count */}
              <div className="absolute bottom-3 right-3">
                <span className="text-xs text-gray-400 dark:text-gray-400 bg-white/10 dark:bg-white/10 px-2 py-1 rounded-md">
                  {characterDirection.length}/500
                </span>
              </div>
            </div>

            <div className="space-y-3 flex flex-col items-center">
              <button
                onClick={handleStartGeneration}
                disabled={!characterDirection.trim() || isStartingGeneration}
                className="w-3/4 md:w-1/2 bg-white text-black hover:bg-gray-200 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium transition-all duration-200"
              >
                {isStartingGeneration ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </div>
                ) : (
                  "Generate Video"
                )}
              </button>

              {/* Generation Info */}
              <div className="text-center text-sm text-gray-600 dark:text-white">
                <div className="flex items-center justify-center gap-4">
                  <span>
                    Est.{" "}
                    {batchQuantity > 1
                      ? `${getEstimatedTime()} × ${batchQuantity}`
                      : getEstimatedTime()}
                  </span>
                  {!isFreePlan && (
                    <div className="flex items-center gap-1">
                      <svg
                        className="w-3 h-3 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5.5H11.5v1.15c-1.38.21-2.58 1.08-2.58 2.54 0 1.54 1.17 2.42 3.16 2.96 1.77.48 2.24 1.12 2.24 1.83 0 .78-.79 1.52-2.1 1.52-1.63 0-2.24-.84-2.32-1.64H8.19c.1 1.7 1.36 2.66 2.98 2.97V18.5h1.73v-1.19c1.42-.21 2.63-1.03 2.63-2.58 0-1.82-1.34-2.66-3.22-3.09z" />
                      </svg>
                      <span>
                        {batchQuantity > 1
                          ? `${
                              getCreditsRequired() * batchQuantity
                            } credits (${batchQuantity} × ${getCreditsRequired()})`
                          : `${getCreditsRequired()} credits`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (propMode === "i2v") {
      return (
        <div className="space-y-6">
          {/* Page Header */}
          <div className="text-left mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Image to Video
            </h1>
            <p className="text-gray-600 dark:text-white text-lg">
              Bring your images to life by converting static photos into dynamic
              video content.
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Model Selection - Only show for authenticated users */}
            {isSignedIn && (
              <div>
                <ModelSelector
                  selectedModel={selectedModel}
                  onModelChange={handleModelChange}
                  type={getModelType()}
                  disabled={noFilterMode}
                  defaultModelInfo={DEFAULT_I2V_MODEL}
                />

                {/* Upgrade to WAN 2.5 Notice - Only for Creator+ users with wan2.2-nofilter */}
                {hasCreatorPlanOrHigher() &&
                  selectedModel === "wan2.2-nofilter" && (
                  <div className="mt-3 bg-gradient-to-br from-yellow-500/5 via-amber-500/5 to-orange-500/5 border-2 border-yellow-500/40 rounded-xl p-4 flex items-center justify-between gap-4 hover:border-yellow-500/60 hover:shadow-[0_0_20px_rgba(234,179,8,0.15)] transition-all duration-300">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-yellow-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm mb-0.5 flex items-center gap-2">
                          Advanced No Filter Model
                          <span className="bg-yellow-500/20 text-yellow-300 text-xs px-2 py-0.5 rounded-full font-bold border border-yellow-500/30">
                            NEW
                          </span>
                        </p>
                        <p className="text-gray-300 text-xs">
                          Enhanced audio, higher quality, faster generation
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedModel("wan2.5")}
                      className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold px-4 py-2 rounded-lg hover:from-yellow-400 hover:to-amber-400 transition-all duration-200 whitespace-nowrap text-sm hover:shadow-lg"
                    >
                      Switch Now
                    </button>
                  </div>
                )}

              {/* WAN 2.5 Upgrade Notice - Only for Starter plan users */}
              {hasStarterPlan() && (
                <div className="mt-3 bg-gradient-to-br from-yellow-500/5 via-amber-500/5 to-orange-500/5 border-2 border-yellow-500/40 rounded-xl p-4 flex items-center justify-between gap-4 hover:border-yellow-500/60 hover:shadow-[0_0_20px_rgba(234,179,8,0.15)] transition-all duration-300">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-sm mb-0.5 flex items-center gap-2">
                        Advanced No Filter Model WITH AUDIO
                        <span className="bg-yellow-500/20 text-yellow-300 text-xs px-2 py-0.5 rounded-full font-bold border border-yellow-500/30">
                          NEW
                        </span>
                      </p>
                      <p className="text-gray-300 text-xs">
                        Upgrade to Creator Plan or higher to access
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      window.location.href = "https://pixelmaker.ai?utm_source=pixelmaker_io";
                    }}
                    className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold px-4 py-2 rounded-lg hover:from-yellow-400 hover:to-amber-400 transition-all duration-200 whitespace-nowrap text-sm hover:shadow-lg"
                  >
                    Upgrade Now
                  </button>
                </div>
              )}
              </div>
            )}

            {/* Aspect Ratio Selection - Only for Veo3 and Veo3.1 */}
            {(selectedModel === "veo3" || selectedModel === "veo3.1") && (
              <div>
                <AspectRatioSelector
                  selectedAspectRatio={aspectRatio}
                  onAspectRatioChange={setAspectRatio}
                />
              </div>
            )}

            {/* Image Upload/Selection */}
            <div>
              {isSignedIn && (
                <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                  Reference Image
                </label>
              )}
              {!referenceImage && (
                <>
                  {isSignedIn && (
                    <button
                      onClick={() => setShowVideoSelectionModal(true)}
                      className="mb-3 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      Or select from assets
                    </button>
                  )}
                  <div className="flex gap-4 items-center">
                    {/* Upload Image - 2/3 width */}
                    <button
                      onClick={() =>
                        document.getElementById("image-upload")?.click()
                      }
                      className="flex-[2] border-2 border-dashed border-white/20 dark:border-white/20 rounded-lg p-4 text-center hover:border-white/40 dark:hover:border-white/40 hover:bg-white/5 dark:hover:bg-white/5 transition-colors"
                    >
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 font-medium">
                        Upload Image
                      </p>
                      <div className="relative h-20 flex items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-600 dark:text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </button>

                    {/* Or text */}
                    <span className="text-gray-400 dark:text-gray-500 text-sm font-medium">or</span>

                    {/* Select an Actor - 1/3 width */}
                    <button
                      onClick={() => setShowBaddieModal(true)}
                      className="flex-1 border-2 border-dashed border-white/20 dark:border-white/20 rounded-lg p-4 text-center hover:border-white/40 dark:hover:border-white/40 hover:bg-white/5 dark:hover:bg-white/5 transition-colors overflow-hidden"
                    >
                      <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 font-medium">
                        Select an Actor
                      </p>
                      {/* Preview of 3 models with overlapping squares */}
                      <div className="relative h-20 flex items-center justify-center">
                        <img
                          src="/baddies/baddie1.jpg"
                          alt="Preview 1"
                          className="absolute w-12 h-12 rounded-md object-cover border-2 border-white dark:border-gray-800 shadow-md transform -rotate-12 -translate-x-6"
                          style={{ zIndex: 1 }}
                        />
                        <img
                          src="/baddies/baddie2.jpg"
                          alt="Preview 2"
                          className="relative w-14 h-14 rounded-md object-cover border-2 border-white dark:border-gray-800 shadow-lg"
                          style={{ zIndex: 3 }}
                        />
                        <img
                          src="/baddies/baddie3.jpg"
                          alt="Preview 3"
                          className="absolute w-12 h-12 rounded-md object-cover border-2 border-white dark:border-gray-800 shadow-md transform rotate-12 translate-x-6"
                          style={{ zIndex: 2 }}
                        />
                      </div>
                    </button>
                  </div>
                </>
              )}
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      // Validate file size (max 10MB for mobile compatibility)
                      const maxSize = 10 * 1024 * 1024; // 10MB
                      if (file.size > maxSize) {
                        showToast?.({ type: "error", title: "Image is too large. Please use an image smaller than 10MB." });
                        console.error("File too large:", file.size, "bytes");
                        return;
                      }

                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        showToast?.({ type: "error", title: "Please select a valid image file." });
                        console.error("Invalid file type:", file.type);
                        return;
                      }

                      console.log("📤 Uploading image:", {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        userAgent: navigator.userAgent,
                      });

                    const reader = new FileReader();
                      
                      reader.onload = (e) => {
                        try {
                          const result = e.target?.result as string;
                          if (result) {
                            console.log("✅ Image loaded successfully, size:", result.length);
                            setReferenceImage(result);
                          } else {
                            throw new Error("FileReader result is empty");
                          }
                        } catch (error) {
                          console.error("❌ Error processing image result:", error);
                          showToast?.({ type: "error", title: "Failed to process image. Please try a different image." });
                        }
                      };
                      
                      reader.onerror = (error) => {
                        console.error("❌ FileReader error:", error);
                        console.error("Error details:", {
                          error: reader.error,
                          userAgent: navigator.userAgent,
                          fileSize: file.size,
                          fileType: file.type,
                        });
                        showToast?.({ type: "error", title: "Failed to read image file. Please try again." });
                      };

                      reader.onabort = () => {
                        console.error("❌ FileReader aborted");
                        showToast?.({ type: "error", title: "Image upload was cancelled. Please try again." });
                      };

                    reader.readAsDataURL(file);
                    } catch (error) {
                      console.error("❌ Error setting up file reader:", error);
                      showToast?.({ type: "error", title: "Failed to upload image. Please try again." });
                    }
                  }
                }}
                className="hidden"
              />
            </div>

            {/* Show selected image or video reference */}
            {referenceImage && (
              <div className="flex justify-center">
                <div className="border border-white/20 dark:border-white/20 rounded-lg p-4 bg-white/5 dark:bg-white/5 transition-colors inline-block max-w-full">
                  {referenceImage.startsWith("http") && linkedVideo ? (
                    <div className="text-center">
                      <div className="bg-white/10 rounded-lg p-4 mb-3">
                        <svg
                          className="w-8 h-8 text-white mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-white font-medium">
                          Video Selected as Reference
                        </p>
                        <p className="text-gray-300 text-sm">
                          The API will extract the appropriate frame during
                          generation
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setReferenceImage(null);
                          setLinkedVideo(null);
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove Video Reference
                      </button>
                    </div>
                  ) : (
                    <div className="relative inline-block max-w-full">
                      <img
                        src={referenceImage}
                        alt="Selected"
                        className="h-[400px] w-auto object-cover rounded-lg"
                      />
                      <button
                        onClick={() => {
                          setReferenceImage(null);
                        }}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                        aria-label="Remove image"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Animation Prompt */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-3">
                What would you like to see?
              </label>
              <textarea
                value={characterDirection}
                onChange={(e) => setCharacterDirection(e.target.value)}
                placeholder={
                  location.state &&
                  (location.state as CreateVideoState).generatedImage
                    ? "Describe how you want your generated image to be animated. For example: The character starts walking slowly, then breaks into a run."
                    : "Describe how you want the image to be animated. For example: The character starts walking slowly, then breaks into a run."
                }
                className="w-full h-32 px-6 py-4 bg-white/5 dark:bg-white/5 border border-white/20 dark:border-white/20 rounded-xl focus:outline-none focus:ring-0 focus:border-white/40 dark:focus:border-white/40 resize-none text-base leading-relaxed text-white dark:text-white placeholder-gray-400 dark:placeholder-gray-400 transition-all duration-200"
              />

              {/* Character count */}
              <div className="absolute bottom-3 right-3">
                <span className="text-xs text-gray-400 dark:text-gray-400 bg-white/10 dark:bg-white/10 px-2 py-1 rounded-md">
                  {characterDirection.length}/500
                </span>
              </div>
            </div>

            {/* AI Prompt Enhancer Toggle - Only for authenticated users with wan2.2-nofilter and wan2.5 */}
            {isSignedIn &&
              (selectedModel === "wan2.2-nofilter" ||
                selectedModel === "wan2.5") && (
                <div className="relative">
                  <div className="relative bg-gradient-to-r from-white/5 via-white/10 to-white/5 dark:from-white/5 dark:via-white/10 dark:to-white/5 border-2 border-white/20 dark:border-white/20 rounded-xl p-5 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-white/10 dark:hover:shadow-white/10">
                    <div className="flex items-center justify-between relative z-10">
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <label className="block text-base font-bold text-white dark:text-white mb-1">
                              AI Prompt Enhancer
                            </label>
                            <p className="text-xs text-white dark:text-white font-medium">
                              Magically enhance your prompt with AI for best
                              results
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPromptEnhancer(!promptEnhancer)}
                        className={`relative inline-flex items-center h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border border-transparent transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-md ${
                          promptEnhancer
                            ? "bg-gradient-to-r from-blue-500 to-cyan-500 shadow-blue-300 dark:shadow-blue-500/50"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                        style={{ minHeight: "unset", minWidth: "unset" }}
                        role="switch"
                        aria-checked={promptEnhancer}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-all duration-300 ease-in-out flex items-center justify-center text-xs ${
                            promptEnhancer ? "translate-x-5" : "translate-x-0.5"
                          }`}
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

            <div className="space-y-3 flex flex-col items-center">
              <button
                onClick={handleStartGeneration}
                disabled={
                  !referenceImage ||
                  !characterDirection.trim() ||
                  isStartingGeneration
                }
                className="w-3/4 md:w-1/2 bg-white text-black hover:bg-gray-200 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium transition-all duration-200"
              >
                {isStartingGeneration ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </div>
                ) : (
                  "Generate Video"
                )}
              </button>

              {/* Generation Info */}
              <div className="text-center text-sm text-gray-600 dark:text-white">
                <div className="flex items-center justify-center gap-4">
                  <span>
                    Est.{" "}
                    {batchQuantity > 1
                      ? `${getEstimatedTime()} × ${batchQuantity}`
                      : getEstimatedTime()}
                  </span>
                  {!isFreePlan && (
                    <div className="flex items-center gap-1">
                      <svg
                        className="w-3 h-3 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5.5H11.5v1.15c-1.38.21-2.58 1.08-2.58 2.54 0 1.54 1.17 2.42 3.16 2.96 1.77.48 2.24 1.12 2.24 1.83 0 .78-.79 1.52-2.1 1.52-1.63 0-2.24-.84-2.32-1.64H8.19c.1 1.7 1.36 2.66 2.98 2.97V18.5h1.73v-1.19c1.42-.21 2.63-1.03 2.63-2.58 0-1.82-1.34-2.66-3.22-3.09z" />
                      </svg>
                      <span>
                        {batchQuantity > 1
                          ? `${
                              getCreditsRequired() * batchQuantity
                            } credits (${batchQuantity} × ${getCreditsRequired()})`
                          : `${getCreditsRequired()} credits`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Fallback to original form for backward compatibility
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 transition-colors">
        <p className="text-center text-gray-500 dark:text-white">
          Please select a creation mode from the tabs above.
        </p>
      </div>
    );
  };

  // Show form view
  return (
    <div className="bg-black dark:bg-black p-6 pb-48 md:pb-24 transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        {/* Content Policy Error Notice - disabled in showcase */}
        {false && generationError && isContentPolicyError(generationError) && (
          <div className="mb-8 max-w-3xl mx-auto">
            <div className="bg-black border-2 border-white/20 rounded-xl p-6 shadow-[0_0_30px_rgba(255,255,255,0.15)]">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Content Policy Restriction
                </h3>
                <p className="text-white/70 dark:text-white/70 mb-6 max-w-md mx-auto">
                  Your prompt contains content that violates the AI model's
                  policies.
                </p>
                <div className="bg-white/5 border border-white/20 rounded-xl p-6 max-w-lg mx-auto">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="text-base font-semibold text-white">
                        💡 Select the <strong>No Filter</strong> model to
                        generate anything!
                      </p>
                      <p className="text-sm text-white/70 dark:text-white/70">
                        The No Filter model has fewer content restrictions
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedModel("wan2.2");
                      resetVideoGeneration();
                    }}
                    className="w-full px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-gray-200 transition-all duration-200"
                  >
                    Switch to No Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {renderWorkflow()}

        {/* WAN 2.5 Upgrade Modal */}
        {showWan25UpgradeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black dark:bg-black border-2 border-white/20 rounded-xl shadow-[0_0_30px_rgba(255,255,255,0.2)] max-w-sm w-full p-8 relative">
              {/* Close button */}
              <button
                onClick={() => setShowWan25UpgradeModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-3">
                  Creator Plan Required
                </h2>
                <p className="text-gray-300 text-base leading-relaxed">
                  Upgrade to unlock our most advanced no filter model ever.
                  Create anything without restrictions.
                </p>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    window.location.href = "https://pixelmaker.ai?utm_source=pixelmaker_io";
                  }}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-200"
                >
                  Upgrade Now
                </button>
                <button
                  onClick={() => setShowWan25UpgradeModal(false)}
                  className="w-full bg-white/5 border-2 border-white/20 text-white py-3 px-6 rounded-xl hover:bg-white/10 hover:border-white/40 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showVideoSelectionModal && (
          <VideoSelectionModal
            isOpen={showVideoSelectionModal}
            onClose={() => setShowVideoSelectionModal(false)}
            onSelectVideo={handleSelectLinkedVideo}
          />
        )}

        {showFrameSelector && linkedVideo && (
          <VideoFrameSelector
            videoUrl={linkedVideo.url || ""}
            onFrameSelect={handleFrameSelect}
            onClose={() => setShowFrameSelector(false)}
          />
        )}

        {/* Baddie Selection Modal */}
        <BaddieSelectionModal
          isOpen={showBaddieModal}
          onClose={() => setShowBaddieModal(false)}
          onSelectActor={(imageBase64) => setReferenceImage(imageBase64)}
        />
      </div>
    </div>
  );
};

export default CreateVideo;
